import { useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { FolderHeader } from '@/features/folder/FolderHeader';
import { ProjectFileList } from './ProjectFileList';
import { setItemsForParent, selectItemsForParent } from '@/store/itemsSlice';
import { getProjectFilesNormalized } from '@/api/project';
import { useViewMode } from '@/hooks/useViewMode';
import { readSortQuery } from '@/lib/sortQuery';
import type { NormalizedItem } from '@/components/itemList/types';
import ItemListLoading from '../../components/itemListLoading/ItemListLoading';

interface ProjectFilesViewProps {
  fileListView?: string;
}

export function ProjectFilesView({ fileListView }: ProjectFilesViewProps) {
  // Start from the order in the URL, which the file view passes back when returning to this list
  const location = useLocation();
  const [sortByCriteriaIndex, setSortByCriteriaIndex] = useState(() => readSortQuery(location.search).sortIdx);
  const [sortAscending, setSortAscending] = useState(() => readSortQuery(location.search).ascending);
  const [viewMode, setViewMode] = useViewMode('project-files');
  const { id: projectId, viewType } = useParams<{ id: string; viewType?: string }>();
  const items = useAppSelector(selectItemsForParent(projectId)) as NormalizedItem[] | undefined;
  const dispatch = useAppDispatch();

  const filteredItems = useMemo(() => {
    const view = viewType || fileListView;
    if (!items) return null;
    return view === 'allfiles' ? items : items.filter((i) => i.type === view);
  }, [viewType, fileListView, items]);

  const refreshItems = () => {
    if (!projectId) return;
    setTimeout(() => {
      getProjectFilesNormalized(projectId).then((normalized) => {
        dispatch(setItemsForParent({ parentId: projectId, items: normalized }));
      });
    }, 1000);
  };

  return (
    <div className="mt-4">
      <Link
        to={`/in/project/${projectId}`}
        className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground no-underline hover:text-accent-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to project
      </Link>
      <FolderHeader
        sortCriteriaIndex={sortByCriteriaIndex}
        sortAscending={sortAscending}
        showTypes={[]}
        onSortCriteriaChange={() => setSortByCriteriaIndex((i) => (i >= 2 ? 0 : i + 1))}
        onSortDirectionToggle={() => setSortAscending((v) => !v)}
        onShowTypesChange={() => {}}
        showTypeFilter={false}
        disabled={!filteredItems}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
      {!filteredItems ? (
        <ItemListLoading amount={12} mode={viewMode} />
      ) : (
        <ProjectFileList
          items={filteredItems}
          sortCriteriaIndex={sortByCriteriaIndex}
          sortAscending={sortAscending}
          onRefresh={refreshItems}
          projectId={projectId}
          viewMode={viewMode}
        />
      )}
    </div>
  );
}
